import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  bridgeKeyboardFocus,
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Kbd,
  ROW_RUNGS,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { userRefToEntity } from "#app/lib/entity-visuals";
import type { WorkspaceUser } from "#app/lib/data/workspace-data-provider";
import type { OrgChartUserItem } from "./use-org-chart-data";
import { AnalyticsContext, OrgChartEvent, trackEvent } from "#app/lib/analytics";

const FIELD_WIDTH = 260;
// The shell's first-line contract: a screen's opening row is 8px padding + a
// 28px content line, so this field sits on the same 28px rung the other page
// headers' `xs` actions do — at 32 the org chart's header block stood 4px
// taller than the members and teams headers beside it.
const FIELD_HEIGHT = ROW_RUNGS.compactControl;
// Gap between the field's bottom edge and the detached results card.
const PANEL_GAP = 8;

// Keep the search input focused: a mousedown's default would move focus to
// the pressed button and break arrow-key navigation (Base UI binds it to the
// input). Same guard the global-search row actions use.
function keepInputFocus(e: React.MouseEvent) {
  e.preventDefault();
}

/**
 * A result row: the member (entity chip) with a hover/keyboard-revealed
 * "jump" sub-action — a ↵ icon button with a tooltip naming the action —
 * mirroring the global-search row's trailing-affordance pattern. `highlighted`
 * is the Command root's highlight, which Base UI moves on both arrow keys and
 * pointer hover, so the one flag reveals the affordance for both.
 */
function JumpResultRow({
  user,
  highlighted,
  onSelect,
}: {
  user: WorkspaceUser;
  highlighted: boolean;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={user.userId} onClick={onSelect}>
      <Box
        direction="row"
        align="center"
        gap="sm"
        fill="width"
        justify="between"
        minHeight={28}
      >
        <Box shrink direction="row" align="center" minWidth={0}>
          <EntityTag
            entity={userRefToEntity(user)}
            size="sm"
            weight="regular"
            interactive={false}
          />
        </Box>
        {/* Always mounted at its full footprint (revealed via opacity) so
            hovering never changes the row's height — conditional mounting
            made rows grow when the button appeared. */}
        <Box
          opacity={highlighted ? 100 : 0}
          pointerEvents={highlighted ? undefined : "none"}
          shrink={false}
        >
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-xs"
                  shape="square"
                  variant="outline"
                  aria-label={`Jump to ${user.name ?? "member"}`}
                  onMouseDown={keepInputFocus}
                  // Bubbling would reach the row's own select — same action,
                  // but stop it so the jump fires exactly once.
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                  }}
                />
              }
            >
              <Icon name="corner-down-left" size="sm" color="muted" />
            </TooltipTrigger>
            <TooltipPopup>
              <Box direction="row" align="center" gap="sm">
                <Text size="xs">Jump to member</Text>
                <Kbd>Enter</Kbd>
              </Box>
            </TooltipPopup>
          </Tooltip>
        </Box>
      </Box>
    </CommandItem>
  );
}

/**
 * The org chart header's "Jump to member..." combobox: the field IS the input
 * (type to filter), with a detached results card floating absolutely beneath
 * it — the header row never shifts. One Command root spans the input and the
 * card (the global-search pattern), so arrow-key nav and Enter work from the
 * input; `mode="none"` keeps the highlight from rewriting the query.
 *
 * Single target: picking a member replaces the previous one and collapses the
 * list; the closed field then shows their avatar + name with a clear (×).
 */
export function MemberJumpSearch({
  items,
  value,
  onValueChange,
}: {
  /** The chart's items — results match what's actually on the canvas. */
  items: OrgChartUserItem[];
  value: string | null;
  onValueChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Mirrors Base UI's highlight (arrow keys AND pointer hover) so rows can
  // reveal their jump affordance.
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // A phone has no room beside the page title for the field: it collapses to
  // an icon, and opens over the title from the right edge while searching.
  const isMobile = useIsMobile();
  const collapsed = isMobile && !open;

  const members = useMemo(
    () =>
      items.flatMap((item) =>
        item.data.kind === "user" ? [item.data.user] : [],
      ),
    [items],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((u) =>
      [u.name, u.username, u.email].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [members, query]);

  const selected = useMemo(
    () => (value ? (members.find((u) => u.userId === value) ?? null) : null),
    [members, value],
  );
  const selectedEntity = selected ? userRefToEntity(selected) : null;

  const close = useCallback(() => {
    setOpen(false);
    inputRef.current?.blur();
  }, []);

  // Closing resets the query (so the next open starts from the full list) and
  // drops the stale highlight.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setHighlightedId(null);
    }
  }, [open]);

  // No portal/overlay backs the card — close on any outside pointerdown
  // (capture phase). The listener covers BOTH the field and the detached card
  // because the ref lives on their shared container.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  const handleSelect = useCallback(
    (id: string) => {
      trackEvent({ context: AnalyticsContext.OrgChart, event: OrgChartEvent.JumpToMember });
      onValueChange(id);
      close();
    },
    [onValueChange, close],
  );

  const onItemHighlighted = useCallback((highlighted: unknown) => {
    setHighlightedId(typeof highlighted === "string" ? highlighted : null);
  }, []);

  const onQueryChange = useCallback((next: string) => {
    setQuery(next);
    setOpen(true);
  }, []);

  // The field mounts after the tap that expands it; the bridge keeps the
  // keyboard up until this focus lands.
  useEffect(() => {
    if (!isMobile || !open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isMobile, open]);

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
    },
    [close],
  );

  return (
    // Reserves the collapsed field's footprint; the results card floats
    // beneath it absolutely so the header row never shifts.
    <Box
      ref={containerRef}
      width={isMobile ? FIELD_HEIGHT : FIELD_WIDTH}
      height={FIELD_HEIGHT}
      position="relative"
      zIndex={open ? 50 : undefined}
    >
      {collapsed ? (
        <Button
          size="icon-xs"
          shape="pill"
          color={selected ? "primary" : "neutral"}
          variant={selected ? "outline" : "ghost"}
          aria-label={
            selected
              ? `Jump to member, showing ${selected.name ?? "member"}`
              : "Jump to member"
          }
          onClick={() => {
            bridgeKeyboardFocus();
            setOpen(true);
          }}
        >
          <Icon name="search" size="sm" />
        </Button>
      ) : (
        <Box
          position={isMobile ? "absolute" : "relative"}
          top={isMobile ? 0 : undefined}
          right={isMobile ? 0 : undefined}
          width={FIELD_WIDTH}
          maxWidth={isMobile ? "calc(100vw - 32px)" : undefined}
        >
          <Command
            mode="none"
            autoHighlight={false}
            // Closed-with-selection renders the member as a chip beside an empty
            // input (the chip owns avatar→name spacing); opening swaps to the
            // live query.
            value={open ? query : ""}
            onValueChange={onQueryChange}
            onItemHighlighted={onItemHighlighted}
          >
            {/* The field — its own bordered box, themed while editing (primary
                border; the glyph stays muted), staying a field rather than
                merging with the card. Asymmetric inset: 8px leads the glyph, 2px
                so the trailing × tile sits snug at the field's end. */}
            <Box
              direction="row"
              align="center"
              gap="xs"
              height={FIELD_HEIGHT}
              background="card"
              border
              borderColor={open ? "primary" : "border"}
              rounded="md"
              paddingLeft="sm"
              paddingRight="2xs"
              overflow="hidden"
            >
              {/* One glyph only: ours leads so the selected member chip can sit
                  between it and the input; CommandInput's own icon is dropped via
                  startAddon={null}. */}
              {/* The glyph stays muted while editing — the themed border already
                  says "active", and a second themed mark reads as noise. */}
              <Icon name="search" size="sm" color="muted" />
              {selectedEntity && !open ? (
                // The full chip (avatar + name) — EntityChipShell owns the
                // avatar→name gap; splitting the name into the input stacked the
                // input's start inset onto the row gap and read as a hole.
                <Box shrink minWidth={0}>
                  <EntityTag
                    entity={selectedEntity}
                    size="xs"
                    weight="regular"
                    interactive={false}
                  />
                </Box>
              ) : null}
              {/* flex-1 (basis 0), not grow (basis auto): the input's intrinsic
                  min-content width must not squeeze the chip — closed-with-
                  selection the input is empty and yields all its width. */}
              <Box flex={1}>
                <CommandInput
                  ref={inputRef}
                  autoFocus={false}
                  size="sm"
                  startAddon={null}
                  placeholder={
                    selectedEntity && !open ? undefined : "Jump to member..."
                  }
                  onFocus={() => setOpen(true)}
                  onKeyDown={onInputKeyDown}
                />
              </Box>
              {/* One trailing ×: while editing it clears the query (or closes
                  when already empty); while closed-with-selection it clears the
                  member. */}
              {open || selectedEntity ? (
                <Button
                  size="icon-xs"
                  shape="square"
                  variant="outline"
                  aria-label={
                    open
                      ? query
                        ? "Clear search"
                        : isMobile && selectedEntity
                          ? "Clear member"
                          : "Close"
                      : "Clear member"
                  }
                  onMouseDown={keepInputFocus}
                  onClick={() => {
                    if (open) {
                      if (query) setQuery("");
                      else {
                        // The phone's collapsed icon has no × of its own.
                        if (isMobile && selectedEntity) onValueChange(null);
                        close();
                      }
                    } else {
                      onValueChange(null);
                    }
                  }}
                >
                  <Icon name="x" size="sm" color="muted" />
                </Button>
              ) : null}
            </Box>

            {/* Detached results card — floats below the field with a gap, its own
                rounded corners and popover chrome. maxHeight hands the list's
                ScrollArea a definite bound so long rosters scroll in place. */}
            {open && (
              <Box
                position="absolute"
                top={FIELD_HEIGHT + PANEL_GAP}
                left={0}
                right={0}
                direction="column"
                surface="popover"
                border
                rounded="lg"
                overflow="hidden"
                maxHeight={320}
              >
                <CommandList>
                  {results.length === 0 ? (
                    <Box paddingX="sm" paddingY="sm" align="center">
                      <Text size="sm" variant="muted">
                        No members found
                      </Text>
                    </Box>
                  ) : (
                    results.map((u) => (
                      <JumpResultRow
                        key={u.userId}
                        user={u}
                        highlighted={highlightedId === u.userId}
                        onSelect={() => handleSelect(u.userId)}
                      />
                    ))
                  )}
                </CommandList>
              </Box>
            )}
          </Command>
        </Box>
      )}
    </Box>
  );
}
